import { Link } from "react-router-dom";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useScriptMode } from "../../contexts/ScriptModeContext";
import logo from "../../assets/img/logo-white.png";
import mascot from "../../assets/img/mascot.png";
import BrandLockup from "../../components/brand/BrandLockup";

/**
 * Giriş / kayıt / şifre sayfalarının ortak düzeni.
 * - Geniş ekran (>= 768px): iki panelli cam kart (marka paneli + form).
 * - Dar ekran: mobil uygulamayla aynı; tam genişlik marka paneli, altında kart.
 */
export default function AuthLayout(props) {
  const isDesktop = useMediaQuery("(min-width:768px)");
  return isDesktop ? <DesktopLayout {...props} /> : <MobileLayout {...props} />;
}

function DesktopLayout({ title, subtitle, children, footer }) {
  const { t } = useScriptMode();

  return (

    <div className="relative min-h-screen flex items-center justify-center overflow-hidden bg-terakkiBrandCloud p-4 md:p-8">
      {/* Soft ambient blobs */}
      <div className="pointer-events-none absolute -left-32 -top-32 h-[28rem] w-[28rem] rounded-full bg-terakkiBrandMist blur-3xl opacity-70 motion-safe:animate-float-slow" />
      <div className="pointer-events-none absolute -right-40 top-1/3 h-[32rem] w-[32rem] rounded-full bg-terakkiBrandSoft/40 blur-3xl motion-safe:animate-float-slow [animation-delay:1.5s]" />
      <div className="pointer-events-none absolute -bottom-40 left-1/4 h-[26rem] w-[26rem] rounded-full bg-terakkiBrandMist/80 blur-3xl" />

      <div className="relative w-full max-w-5xl overflow-hidden rounded-[36px] motion-safe:animate-rise-in bg-white/70 ring-1 ring-white/70 shadow-[0_30px_80px_-32px_rgba(0,106,113,0.45)] backdrop-blur-xl">
        <div className="flex flex-col md:flex-row">
          {/* Brand panel */}
          <div className="relative min-w-0 overflow-hidden bg-gradient-to-br from-terakkiBrandDeep via-terakkiBrand to-terakkiBrandSoft p-8 text-white md:w-1/2 md:p-10">
            {/* decorative blobs */}
            <div className="pointer-events-none absolute -left-24 -bottom-24 h-72 w-72 rounded-full bg-white/10 blur-sm" />
            <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-white/10 blur-sm" />
            {/* soft shine sweep */}
            <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(90deg,rgba(255,255,255,0.06),rgba(255,255,255,0.18),rgba(255,255,255,0.06))] bg-[length:200%_200%] animate-hero-shine motion-reduce:animate-none" />

            <img
              src={logo}
              alt={t("app_name")}
              className="pointer-events-none absolute right-5 top-5 h-24 w-auto select-none object-contain opacity-95 drop-shadow-[0_12px_30px_rgba(0,0,0,0.18)] md:right-7 md:top-7 md:h-32 lg:h-36"
            />

            <div className="relative mb-8">
            <Link
              to="/"
              className="group inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/10 px-4 py-2 text-base font-semibold text-white backdrop-blur-sm transition-colors duration-200 hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="h-4 w-4 transition-transform duration-200 group-hover:-translate-x-0.5"
                aria-hidden="true"
              >
                <line x1="19" y1="12" x2="5" y2="12" />
                <polyline points="12 19 5 12 12 5" />
              </svg>
              {t("auth_back_to_site")}
            </Link>
            </div>

            <div className="relative">
              <p className="text-base font-semibold tracking-[0.28em] text-white/75 animate-hero-in [animation-delay:.05s] motion-reduce:animate-none">
                {t("auth_welcome")}
              </p>
              {/* Marka: Adım Adım Terakki */}
              <h1 className="mt-4 animate-hero-in [animation-delay:.12s] motion-reduce:animate-none">
                <BrandLockup tone="dark" size="lg" />
              </h1>
              <div className="mt-4 h-1 w-16 rounded-full bg-white/40 animate-hero-in [animation-delay:.18s] motion-reduce:animate-none" />
              <p className="mt-5 max-w-md pr-24 text-base leading-7 text-white/90 animate-hero-in [animation-delay:.3s] motion-reduce:animate-none md:pr-36 md:text-md">
                {t("auth_brand_description")}
              </p>
            </div>

            {/* Mascot — bottom right of the brand panel */}
            <img
              src={mascot}
              alt=""
              aria-hidden="true"
              className="pointer-events-none absolute -bottom-4 -right-3 w-32 select-none animate-hero-float motion-reduce:animate-none md:w-44 lg:w-48"
            />
          </div>

          {/* Form panel */}
          <div className="relative flex min-w-0 items-center bg-white/95 p-8 md:w-1/2 md:p-12">
            <div className="mx-auto w-full max-w-md">
              <h2 className="text-3xl md:text-4xl font-extrabold text-terakkiInk break-words motion-safe:animate-hero-in [animation-delay:.15s]">
                {title}
              </h2>
              {subtitle && (
                <p className="mt-2 text-base text-terakkiNeutral motion-safe:animate-hero-in [animation-delay:.22s]">{subtitle}</p>
              )}
              <div className="mt-8 motion-safe:animate-hero-in [animation-delay:.3s]">{children}</div>
              {footer && (
                <div className="mt-6 text-base text-terakkiNeutral motion-safe:animate-hero-in [animation-delay:.4s]">{footer}</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function MobileLayout({ title, subtitle, children, footer }) {
  const { t } = useScriptMode();

  return (

    <div className="relative min-h-screen overflow-hidden bg-terakkiBrandCloud">
      {/* Soft ambient blobs */}
      <div className="pointer-events-none absolute -left-32 top-1/3 h-[28rem] w-[28rem] rounded-full bg-terakkiBrandMist blur-3xl opacity-70 motion-safe:animate-float-slow" />
      <div className="pointer-events-none absolute -right-40 bottom-0 h-[32rem] w-[32rem] rounded-full bg-terakkiBrandSoft/40 blur-3xl motion-safe:animate-float-slow [animation-delay:1.5s]" />

      {/* Brand panel — full width, rounded bottom */}
      <header className="relative overflow-hidden rounded-b-[36px] bg-gradient-to-br from-terakkiBrandDeep via-terakkiBrand to-terakkiBrandSoft px-6 pb-12 pt-6 text-white shadow-[0_24px_60px_-28px_rgba(0,106,113,0.55)] md:px-10 md:pb-16 md:pt-8">
        <div className="pointer-events-none absolute -left-24 -bottom-24 h-72 w-72 rounded-full bg-white/10 blur-sm" />
        <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-white/10 blur-sm" />
        <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(90deg,rgba(255,255,255,0.06),rgba(255,255,255,0.18),rgba(255,255,255,0.06))] bg-[length:200%_200%] animate-hero-shine motion-reduce:animate-none" />

        <div className="relative mx-auto w-full max-w-3xl">
          <div className="flex items-start justify-between gap-4">
            <Link
              to="/"
              className="group inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/10 px-4 py-2 text-base font-semibold text-white backdrop-blur-sm transition-colors duration-200 hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="h-4 w-4 transition-transform duration-200 group-hover:-translate-x-0.5"
                aria-hidden="true"
              >
                <line x1="19" y1="12" x2="5" y2="12" />
                <polyline points="12 19 5 12 12 5" />
              </svg>
              {t("auth_back_to_site")}
            </Link>
            <img
              src={logo}
              alt={t("app_name")}
              className="pointer-events-none h-24 w-auto select-none object-contain opacity-95 drop-shadow-[0_12px_30px_rgba(0,0,0,0.18)] md:h-32"
            />
          </div>

          <div className="relative mt-2 pr-20 sm:pr-28 md:pr-44">
            <p className="text-sm font-semibold tracking-[0.28em] text-white/75 animate-hero-in [animation-delay:.05s] motion-reduce:animate-none">
              {t("auth_welcome")}
            </p>
            {/* Marka: Adım Adım Terakki */}
            <h1 className="mt-3 animate-hero-in [animation-delay:.12s] motion-reduce:animate-none">
              <BrandLockup tone="dark" size="lg" />
            </h1>
            <div className="mt-3 h-1 w-16 rounded-full bg-white/40 animate-hero-in [animation-delay:.18s] motion-reduce:animate-none" />
            <p className="mt-4 max-w-md text-base leading-7 text-white/90 animate-hero-in [animation-delay:.3s] motion-reduce:animate-none md:text-md">
              {t("auth_brand_description")}
            </p>
          </div>

          {/* Mascot — bottom right of the brand panel */}
          <img
            src={mascot}
            alt=""
            aria-hidden="true"
            className="pointer-events-none absolute -bottom-14 -right-2 w-32 select-none animate-hero-float motion-reduce:animate-none md:-bottom-20 md:w-44"
          />
        </div>
      </header>

      {/* Form card — overlaps the panel like the mobile card */}
      <main className="relative -mt-6 px-4 pb-10 md:px-8">
        <div className="mx-auto w-full max-w-md rounded-[28px] bg-white p-5 sm:p-6 motion-safe:animate-rise-in [animation-delay:.1s] shadow-[0_24px_60px_-24px_rgba(0,106,113,0.35)] ring-1 ring-terakkiBrandMist md:p-8">
          <h2 className="text-3xl font-extrabold text-terakkiInk break-words motion-safe:animate-hero-in [animation-delay:.2s]">{title}</h2>
          {subtitle && (
            <p className="mt-2 text-base text-terakkiNeutral motion-safe:animate-hero-in [animation-delay:.26s]">{subtitle}</p>
          )}
          <div className="mt-6 motion-safe:animate-hero-in [animation-delay:.32s]">{children}</div>
          {footer && (
            <div className="mt-6 text-base text-terakkiNeutral motion-safe:animate-hero-in [animation-delay:.4s]">{footer}</div>
          )}
        </div>
      </main>
    </div>
  );
}
